import {navigateToUrl, urls} from 'helpers/url'
import {isEmpty, isNil, get} from 'lodash'
import {userHasFeature, isManagementUserAccountType, userIsPodOnly, isExpired} from "helpers/user"
import {features} from "helpers/constants"
import {getLastLocation} from 'helpers/history'

export const normalizePathname = (pathname) =>
  pathname.replace(/\/$/, '') || '/'

export const excludedPathsFromLocationMemory = [
  '/activate-user',
  '/change-plan',
  '/forgot',
  '/login',
  '/reset-password',
  '/sign-up',
  '/tfasetup',
]

export const excludedPathsFromBacklinkNavigation = [
  '/',
  '/accept',
  '/activate-user',
  '/complete-order',
  '/customer',
  '/forgot',
  '/login',
  '/reset-password',
  '/sign-up',
  '/sign-up-success',
]

export const getBasePath = (mainRoute) => {
  switch (mainRoute) {
    default:
    case 'contacts':
      return urls.contacts.manageContactsUrl
    case 'quoting':
      return urls.quoting.viewQuote
    case 'purchasing':
      return urls.purchasing.viewPurchaseOrder
  }
}

export const getTabPath = (mainRoute, tab, id, selectedQuoteId, selectedPurchaseOrderId) => {
  switch (mainRoute) {
    default:
    case 'contacts':
      return `/${tab}${id}`

    case 'quoting':
      return isNil(selectedQuoteId)
        ? `/${tab}${id}`
        : `/quotes/${selectedQuoteId}/${tab}${id}`

    case 'purchasing':
      return isNil(selectedPurchaseOrderId)
        ? `/${tab}${id}`
        : `/purchase-orders/${selectedPurchaseOrderId}/${tab}${id}`
  }
}

export const getNewPathForCompanyContactChange = (mainRoute, companyId, contactId, tab) => {
  const basePath = getBasePath(mainRoute)

  return !isNil(contactId)
    ? `${basePath}/contact/${contactId}/${tab}`
    : `${basePath}/company/${companyId}/${tab}`
}

export const getNewPathForTabChange = (
  mainRoute,
  tab,
  id,
  curCompanyId,
  curContactId,
  curQuoteId,
  curPurchaseOrderId
) => {
  id = typeof id === 'string' && id.length > 0 ? `#${id}` : ''
  const basePath = getBasePath(mainRoute)
  const tabPath = getTabPath(mainRoute, tab, id, curQuoteId, curPurchaseOrderId)

  return !isNil(curContactId)
    ? `${basePath}/contact/${curContactId}${tabPath}`
    : `${basePath}/company/${curCompanyId}${tabPath}`
}

export const getPreviewPath = () => {
  return '/preview/preview'
}

export const getPathForQuote = (quoteId, companyId, contactId, currentTab) => {
  const basePath = urls.quoting.viewQuote
  const module = 'quotes'
  const tab = currentTab ? `/${currentTab}` : ''

  return isNil(contactId)
    ? `${basePath}/company/${companyId}/${module}/${quoteId}${tab}`
    : `${basePath}/contact/${contactId}/${module}/${quoteId}${tab}`
}

export const getPathForQuotePreview = (quoteId, companyId, contactId) => {
  const previewPath = getPreviewPath()
  return `${getPathForQuote(quoteId, companyId, contactId)}${previewPath}`
}

export const getPathForPurchaseOrder = (purchaseOrderId, companyId, contactId, currentTab) => {
  const basePath = urls.purchasing.viewPurchaseOrder

  const module = 'purchase-orders'
  const tab = currentTab ? `/${currentTab}` : ''

  return isNil(contactId)
    ? `${basePath}/company/${companyId}/${module}/${purchaseOrderId}${tab}`
    : `${basePath}/contact/${contactId}/${module}/${purchaseOrderId}${tab}`
}

export const getPathForPurchaseOrderPreview = (purchaseOrderId, companyId, contactId) => {
  const previewPath = getPreviewPath()
  return `${getPathForPurchaseOrder(purchaseOrderId, companyId, contactId)}${previewPath}`
}

export const getPathForInvoice = (invoiceId) => {
  return `${urls.invoicing.baseUrl}/invoices/${invoiceId}`
}

export const getPathForInvoicePreview = (invoiceId, companyId, contactId) => {
  return `${getPathForInvoice(invoiceId, companyId, contactId)}/preview`
}

export const getPathForPurchaseInvoice = (purchaseInvoiceId) => {
  return `${urls.invoicing.baseUrl}/purchasing/purchase-invoice/${purchaseInvoiceId}`
}

export const shouldMemoriseRoute = (route, useMemory) => {
  return useMemory && route.memorise !== false
}

export const getDefaultRoute = (routes, currentRoute, useMemory) => {
  if (useMemory && currentRoute) return currentRoute
  const defaultRoute = routes.find(route => route.isDefault)
  if (defaultRoute) return defaultRoute.path
  return isEmpty(routes) ? null : routes[0].path
}

export const isJobBoardDefaultRoute = (user) => {
  return !userHasFeature(user, features.CONTACTS_LICENCE) && userHasFeature(user, features.PRODUCTION_LICENCE)
}

export const getRedirectUrl = (user, followBackLink = true) => {
  const username = get(user, 'username', null)
  const v3Direct = get(user, 'v3Direct', true)
  const backLink = window.location.pathname

  if (isManagementUserAccountType(user))
    return urls.myAccount.userUrl

  if (userIsPodOnly(user))
    return urls.settings.generalUrl

  if (isExpired(user))
    return urls.myAccount.changePlan

  if (followBackLink && !isNil(backLink) && !excludedPathsFromBacklinkNavigation.includes(normalizePathname(backLink)))
    return backLink + window.location.search + window.location.hash

  if (v3Direct) {
    return getLastLocation(username)
  } else {
    navigateToUrl(get(user, 'primaryFrontend', null))
  }

  return '/'
}
